import { makeStyles } from 'tss-react/mui';

/** Modal size breakpoints (px): SX 444, S 600, M 960, L 1280, XL 1920 — shared with View Task modal */
export const TASK_MODAL_WIDTH_SX = 444;
export const TASK_MODAL_WIDTH_S = 600;
export const TASK_MODAL_WIDTH_M = 960;
export const TASK_MODAL_WIDTH_L = 1280;
export const TASK_MODAL_WIDTH_XL = 1920;

export const CreateTaskNewStyles = makeStyles()(theme => ({
  modalContainer: {
    '& .MuiDialog-paper': {
      overflow: 'visible',
      // SX - 444px (viewport < 600px)
      maxWidth: TASK_MODAL_WIDTH_SX,
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        // S - 600px (600px ≤ viewport < 960px)
        maxWidth: TASK_MODAL_WIDTH_S,
      },
      [theme.breakpoints.up('md')]: {
        // M - 960px (960px ≤ viewport < 1280px)
        maxWidth: TASK_MODAL_WIDTH_M,
      },
      [theme.breakpoints.up('lg')]: {
        // L - 1280px (1280px ≤ viewport < 1920px)
        maxWidth: TASK_MODAL_WIDTH_L,
      },
      [theme.breakpoints.up('xl')]: {
        // XL - 1920px (viewport ≥ 1920px)
        maxWidth: TASK_MODAL_WIDTH_XL,
      },
    },
    /** When GenericDialog toggles fullscreen, ensure the paper truly fills the viewport */
    '& .MuiDialog-paperFullScreen': {
      maxWidth: '100% !important',
      width: '100% !important',
      height: '100% !important',
      margin: 0,
      borderRadius: 0,
    },
  },
  dialogContent: {
    padding: '24px',
    backgroundColor: theme.palette.background.paper,
  },
  /** Matches GenericDialog primary action spacing (nonTertiaryButton marginLeft) */
  saveDialogPrimaryAction: {
    marginLeft: 16,
  },
  saveDialogButtonLabelWrap: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Stretch with MUI Button label row so the spinner overlay matches full button height */
  saveDialogButtonLabelWrapWhenLoading: {
    alignSelf: 'stretch',
  },
  saveDialogButtonTextHidden: {
    visibility: 'hidden',
  },
  /** Flex-center spinner (avoids translate offset from line-height / SVG metrics) */
  saveDialogButtonSpinnerOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  contentGrid: {
    marginTop: 0,
  },
  section: {
  },
  fieldLabel: {
    display: 'block',
    marginBottom: 4,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  /** Label and value on the same line (e.g. From, To, CC) */
  fromToCcRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'nowrap',
  },
  fromToCcLabel: {
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    minWidth: 45,
  },
  fromToCcValue: {
    flex: 1,
    minWidth: 0,
  },
  fromToRow: {
    minHeight: 40,
    padding: '8px 12px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  placeholderFromTo: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  /** Subject field: same row layout and height/padding as From/To/CC */
  subjectInputRow: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      minHeight: 24,
      padding: 0,
      alignItems: 'center',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiInputBase-input': {
      padding: 0,
      fontSize: 14,
    },
  },
  input: {
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  /** Category, Follow-Up Date, Priority + checkboxes: single row, grey inputs */
  categoryPriorityCheckboxRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
  },
  categoryPriorityInput: {
    minWidth: 120,
    flex: '1 1 120px',
    '& .MuiFilledInput-root, & .MuiInputBase-root': {
      backgroundColor: theme.palette.grey[100],
      minHeight: 56,
      height: 56,
      boxSizing: 'border-box',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    /** Keep label grey when focused (no blue) */
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.text.secondary,
    },
  },
  /**
   * Wrapper so we can target DateRangePicker FormControl and force same height as Category/Priority.
   * IMPORTANT: Selectors are limited to the FormControl subtree (the text field only) so that
   * the calendar popup's month/year Selects are NOT affected by these !important rules.
   * Otherwise .MuiInputBase-root / .MuiInputBase-input would apply to the calendar and break alignment.
   */
  followUpDatePickerWrapper: {
    minWidth: 120,
    flex: '1 1 120px',
    maxWidth: 160,
    '& .MuiFormControl-root': {
      margin: 0,
      width: '100%',
    },
    /** Only the Follow-Up Date text field (inside FormControl), not the calendar popup */
    '& .MuiFormControl-root .MuiInputBase-root': {
      backgroundColor: `${theme.palette.grey[100]} !important`,
      borderRadius: 0,
      boxShadow: 'none',
      border: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      minHeight: '56px !important',
      height: '56px !important',
      maxHeight: '56px !important',
      boxSizing: 'border-box',
      paddingTop: '4px !important',
      paddingBottom: '4px !important',
      paddingLeft: '12px !important',
      paddingRight: '12px !important',
      display: 'flex !important',
      flexDirection: 'row' as const,
      flexWrap: 'nowrap',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: `${theme.palette.grey[100]} !important`,
      },
    },
    '& .MuiFormControl-root .MuiInputLabel-root': {
      marginTop: 0,
      color: theme.palette.text.secondary,
    },
    '& .MuiFormControl-root .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.text.secondary,
    },
    '& .MuiFormControl-root .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.grey[100]} !important`,
      paddingTop: '4px !important',
      paddingBottom: '4px !important',
      paddingLeft: '12px !important',
      paddingRight: '8px !important',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      flex: '1 1 auto',
      minWidth: 0,
    },
    /** Follow-Up Date text field input slot only (not calendar month/year) */
    '& .MuiFormControl-root .MuiInputBase-input': {
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.text.primary,
      padding: '27px 0px 10px !important',
      lineHeight: 1.5,
    },
    /** DateRangePicker icon: remove default margin so height stays 56px; text field only */
    '& .MuiFormControl-root .MuiInputAdornment-root': {
      marginBottom: '0 !important',
      marginTop: '0 !important',
      alignSelf: 'center',
      alignItems: 'center',
      flexShrink: 0,
    },
    '& .MuiFormControl-root .MuiInputAdornment-root *': {
      marginBottom: '0 !important',
      marginTop: '0 !important',
    },
  },
  /** Calendar header month/year selects: align text and dropdown icon (no FormControl here, so not overridden by followUpDatePickerWrapper) */
  followUpDateCalendar: {
    '& .MuiPaper-root .MuiSelect-select': {
      display: 'flex !important',
      alignItems: 'center !important',
      lineHeight: 'normal',
    },
    '& .MuiPaper-root .MuiSelect-icon, & .MuiPaper-root .MuiSelect-iconOutlined': {
      top: '50% !important',
      transform: 'translateY(-50%) !important',
    },
  },
  followUpDateLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    /** Keep label grey when focused (no blue) */
    '&.Mui-focused': {
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    flexShrink: 0,
  },
  inputLabel: {
    fontSize: 14,
  },
  checkboxLabel: {
    marginLeft: 0,
    marginRight: 8,
  },
  /** Message (Description): no label, 10 lines with scroll when exceeded, border top */
  messageSection: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 16,
  },
  messageInput: {
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    '& .MuiFilledInput-root::before': {
      borderBottomColor: theme.palette.divider,
    },
    '& .MuiFilledInput-root::after': {
      borderBottomColor: theme.palette.divider,
    },
    '& .MuiInputBase-input': {
      overflow: 'auto !important',
    },
  },
  dropzone: {
    border: `2px dashed ${theme.palette.primary.light}`,
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 120,
    maxWidth: 240,
    width: '100%',
    boxSizing: 'border-box',
  },
  dropzoneIcon: {
    fontSize: 40,
    color: theme.palette.primary.main,
  },
  dropzoneText: {
    color: theme.palette.primary.main,
    fontSize: 14,
    textAlign: 'center',
  },
  attachmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 6,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  attachmentFileIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    marginRight: 12,
    objectFit: 'contain',
  },
  attachmentName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: 8,
    minWidth: 0,
    color: theme.palette.text.primary,
    fontSize: 14,
  },
  attachmentRemoveBtn: {
    padding: 4,
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
}));
